import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import Table from '@risksmart-app/components/src/table';
import { QuestionnaireTemplateVersionStatus as Questionnaire_Template_Version_Status_Enum } from '@risksmart-app/domain/src/types/consts/questionnaire-template-version-status';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledTextarea from 'src/components/form/controlled-textarea';
import { FormField } from 'src/components/form/form/FormField';
import { useGetQuestionnaireTemplateRegister } from 'src/hooks/queries/questionnaire-template/useGetQuestionnaireTemplateRegister';
import { useGetThirdPartyContacts } from 'src/hooks/queries/third-party/useGetThirdPartyContacts';

import ControlledMultiselect from '../../../../../components/form/controlled-multiselect';
import { useGetCollectionTableProps } from '../../../../questionnaire-templates/config';
import type { QuestionnaireTemplateFields } from '../../../../questionnaire-templates/types';
import type { InvitationFields } from './invitationSchema';

export const InvitationFormFields = () => {
  const { control, setValue } = useFormContext<InvitationFields>();
  const thirdPartyId = useGetGuidParam('id');

  const { data, loading } = useGetQuestionnaireTemplateRegister({
    queryArgs: {},
  });

  // The register returns every template; the legacy query filtered server-side
  // to those with a published version (`versions.Status = published`). Only a
  // published version can be invited on, so the filter is reproduced here.
  const publishedTemplates = useMemo(
    () =>
      data?.questionnaire_template.filter((template) =>
        template.publishedVersion.some(
          (version) =>
            version.Status ===
            Questionnaire_Template_Version_Status_Enum.Published
        )
      ),
    [data]
  );

  const { data: contactsData, loading: contactsLoading } =
    useGetThirdPartyContacts({
      queryArgs: { thirdPartyId },
    });

  // Filter to only include active contacts (not revoked AND has logged in)
  const activeContactOptions = useMemo(() => {
    const contacts = contactsData?.third_party_contact ?? [];

    return contacts
      .filter(
        (contact) =>
          !contact.IsRevoked &&
          // COI-834: the caller org's membership row, not the tenant-global
          // `auth.user."LastSeen"`. Why `[0]` is at most one row, and why an
          // absent row means "never logged in": see the note on
          // `ThirdPartyContactFields.user` in `../../tabs/contacts/types.ts`.
          (contact.user?.organisationusers?.[0]?.LastSeen != null ||
            contact.PasswordSetAtTimestamp != null)
      )
      .map((contact) => ({
        value: contact.Email,
        label: contact.Name
          ? `${contact.Name} (${contact.Email})`
          : contact.Email,
      }));
  }, [contactsData]);

  const versionBidirectionalMap = useMemo(
    () =>
      publishedTemplates?.reduce<Record<string, string>>((acc, curr) => {
        acc[curr.Id] = curr.publishedVersion[0]?.Id;

        return acc;
      }, {}) ?? {},
    [publishedTemplates]
  );

  const tableProps = useGetCollectionTableProps(publishedTemplates);

  const { t } = useTranslation(['common'], {
    keyPrefix: 'plan_questionnaire.fields',
  });
  const [selectedQuestionnaires, setSelectedQuestionnaires] = useState<
    QuestionnaireTemplateFields[]
  >([]);

  useEffect(() => {
    setValue(
      'questionnaires',
      selectedQuestionnaires.map((q) => versionBidirectionalMap[q.Id])
    );
  }, [selectedQuestionnaires, setValue, versionBidirectionalMap]);

  return (
    <div>
      <ControlledMultiselect
        options={activeContactOptions}
        placeholder={t('users_placeholder')}
        testId={'users'}
        name={'users'}
        label={t('users')}
        control={control}
        statusType={contactsLoading ? 'loading' : 'finished'}
      />

      <ControlledTextarea
        name={'message'}
        label={t('message')}
        placeholder={t('message_placeholder')}
        control={control}
      />
      <FormField
        stretch={true}
        label={t('questionnaires', {
          count: selectedQuestionnaires.length,
          total: publishedTemplates?.length ?? 0,
        })}
      >
        <Table
          {...tableProps}
          loading={loading}
          variant={'embedded'}
          selectedItems={selectedQuestionnaires}
          trackBy={'Id'}
          onSelectionChange={({ detail }) => {
            setSelectedQuestionnaires(detail.selectedItems);
          }}
          selectionType={'multi'}
        />
      </FormField>
    </div>
  );
};
